import React, { useState, useEffect } from 'react';
import { Avatar, Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { axiosInstance } from '../../utils/tokenUtils';
import { useTranslation } from '../../i18n/LanguageContext';
import { toast } from 'react-hot-toast';
import ExtendSessionOutcomeDialog from './ExtendSessionOutcomeDialog';

const ExtendSessionRespondDialog = ({ open, onClose, meta, summaryMessage, onAfterAction }) => {
  const { translate } = useTranslation();
  const [busy, setBusy] = useState(null);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (open) {
      setOutcome(null);
    }
  }, [open]);

  const extensionRequestId = meta?.extension_request_id;
  const studentName = meta?.student_name || '—';
  const subject = meta?.subject || '—';
  const extensionMinutes = meta?.extension_minutes ?? '—';
  const extensionFee = meta?.extension_fee;
  const avatarUrl = meta?.student_profile_image;

  const handleRespond = async status => {
    if (busy) return;
    if (!extensionRequestId) return;
    setBusy(status);
    try {
      await axiosInstance.patch(
        `/teachers/session-extension-requests/${extensionRequestId}/action`,
        { status }
      );
      setOutcome({
        variant: status === 'approved' ? 'success' : 'rejected',
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : translate('sessionDetails', 'extensionRespondErrorToast') ||
            'Could not update the request';
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const handleOutcomeClose = () => {
    setOutcome(null);
    onClose();
    if (onAfterAction) onAfterAction();
  };

  const showRequest = open && !outcome;

  return (
    <>
      <Dialog
        open={showRequest}
        onClose={busy ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #EBF0FC 100%)',
            maxWidth: 440,
          },
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
          <Box
            sx={{
              width: 81,
              height: 4,
              bgcolor: '#D5DEE3',
              borderRadius: '12px',
              mx: 'auto',
              mb: 2,
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: 16,
              color: '#14171F',
              mb: 2,
            }}
          >
            {translate('sessionDetails', 'sessionExtendRequestInstructorTitle') ||
              'Session extend request'}
          </Typography>
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              borderRadius: '8px',
              p: 2,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={avatarUrl || undefined}
                variant="rounded"
                sx={{ width: 68, height: 68, borderRadius: '4px', bgcolor: '#D9D9D9' }}
              >
                {!avatarUrl && <PersonOutlineIcon sx={{ fontSize: 32, color: '#fff' }} />}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, color: '#14171F' }}
                >
                  {studentName}
                </Typography>
                <Typography sx={{ fontFamily: 'Poppins', fontSize: 12, color: '#56585E', mt: 0.5 }}>
                  {subject}
                </Typography>
              </Box>
            </Stack>
          </Box>
          {summaryMessage ? (
            <Typography sx={{ fontFamily: 'Poppins', fontSize: 13, color: '#56585E', mb: 2 }}>
              {summaryMessage}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography sx={{ fontFamily: 'Poppins', fontSize: 11, color: '#949DA6' }}>
                {translate('sessionDetails', 'extensionMinutesShortLabel') || 'Extension'}
              </Typography>
              <Typography
                sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, color: '#14171F' }}
              >
                {extensionMinutes} min
              </Typography>
            </Box>
            {extensionFee != null && extensionFee !== '' ? (
              <Box>
                <Typography sx={{ fontFamily: 'Poppins', fontSize: 11, color: '#949DA6' }}>
                  {translate('sessionDetails', 'extensionFeeShortLabel') || 'Koins'}
                </Typography>
                <Typography
                  sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, color: '#14171F' }}
                >
                  {extensionFee}
                </Typography>
              </Box>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!!busy}
              onClick={() => handleRespond('rejected')}
              sx={{
                textTransform: 'none',
                fontFamily: 'Poppins',
                fontWeight: 600,
                borderRadius: '8px',
                py: 1.25,
                borderColor: '#E14949',
                color: '#E14949',
                '&:hover': {
                  borderColor: '#c73d3d',
                  bgcolor: 'rgba(225, 73, 73, 0.06)',
                },
              }}
            >
              {busy === 'rejected'
                ? '…'
                : translate('sessionDetails', 'rejectExtensionBtn') || 'Reject'}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!!busy}
              onClick={() => handleRespond('approved')}
              disableElevation
              sx={{
                textTransform: 'none',
                fontFamily: 'Poppins',
                fontWeight: 600,
                borderRadius: '8px',
                py: 1.25,
                bgcolor: '#0A5EF3',
                '&:hover': { bgcolor: '#0956d9' },
              }}
            >
              {busy === 'approved'
                ? '…'
                : translate('sessionDetails', 'approveExtensionBtn') || 'Approve'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <ExtendSessionOutcomeDialog
        open={Boolean(outcome)}
        onClose={handleOutcomeClose}
        variant={outcome?.variant}
        studentName={studentName === '—' ? '' : studentName}
        extensionMinutes={extensionMinutes}
        extensionFee={extensionFee}
      />
    </>
  );
};

export default ExtendSessionRespondDialog;
